import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';

import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';

import AutoFilledRequests from './components/AutoFilledRequests';
import ProgramList from './components/ProgramList';

import DeclineModal from '../../../components/modals/DeclineModal';
import PreviewModal from '../../../components/modals/PreviewModal';

import Button from '../../../components/controls/Button';
import TextBody from '../../../components/controls/Typography/TextBody';
import BaseModal from '../../../components/modals/BaseModal/BaseModal';
import ReuseRecommendationModal from '../../../components/modals/ReuseRecommendationModal';
import StopReuseRecommendationModal from '../../../components/modals/StopReuseRecommendationModal';
import { apiPOST, apiPUT } from '../../../utils/networking';
import { RecommendationStatus, RecommendationUtil } from '../../../utils/Recommendation';
import { getEvaluationRequestUtils } from '../../../utils/useRecommendation';
import useAuthUtils from '../../../utils/useAuthUtils';
import { useNavigate } from 'react-router-dom';
import useMessages from '../../../utils/useMessages';
import ReopenRecommendationModal from '../../../components/modals/ReopenRecommendationModal/ReopenRecommendationModal';
import onKeyDownEnter from '../../../utils/onKeyDownEnter';
import onKeyDownSpace from '../../../utils/onKeyDownSpace';

import './RequestCard.less';

/**
 * @typedef {import('../../../store/reducers/dashboardReducer').TEvaluationRequest} TEvaluationRequest
 * @param {{
 *  request: TEvaluationRequest;
 *  isReusedForFutureEvaluation: boolean;
 *  disableReuseEvaluation: boolean;
 *  autoFiledRequests: TEvaluationRequest[];
 *  expandedState: boolean;
 *  onReuseStatusChanged?: Function;
 *  onDecline?: Function;
 *  onChanged?: () => void;
 *  highlightWords?: string[];
 * }} props
 * @returns
 */
export const RequestCard = props => {
  /**
   * @type {ReturnType<typeof getEvaluationRequestUtils>}
   */
  const _utils = getEvaluationRequestUtils(props.request);
  const messages = useMessages();
  const navigate = useNavigate();
  const auth = useAuthUtils();
  const [isBusy, set_isBusy] = useState(false);
  const [isExpanded, set_isExpanded] = useState(props.expandedState);
  const [showModalPDF, set_showModalPDF] = useState(false);
  const [showModalReusePrompt, set_showModalReusePrompt] = useState(false);
  const [showModalResueDiscard, set_showModalResueDiscard] = useState(false);
  const [showModalDeclinePrompt, set_showModalDeclinePrompt] = useState(false);
  const [showModalReopenPrompt, set_showModalReopenPrompt] = useState(false);
  const [isDeleted, set_isDeleted] = useState(false);
  const [showModalIsDeleted, set_showModalIsDeleted] = useState(false);

  /**
   *
   * @param {{ result: boolean, message: string }} param0
   * @returns
   */
  const onModalDeclineRequestResult = ({ result, message }) => {
    if (!result) {
      set_showModalDeclinePrompt(false);
      return;
    }

    const applicantName = _utils.getApplicantFullName();
    messages.clearByKeys('Decline');

    set_isBusy(true);
    apiPOST('/recommendation/rest/declineRecommendation/' + props.request.id, message)
      .then(() => {
        messages.notifyYellow(`${applicantName}'s Request is Successfully Declined`, 'Decline');
        set_showModalDeclinePrompt(false);
        set_isBusy(false);
        props.onChanged?.();
      })
      .catch(error => {
        const isNotFoundStatusCodeOccurred = /404/.test(error.status);
        isNotFoundStatusCodeOccurred
          ? void 0
          : messages.notifyRed(
              `Cannot process request at this time. Please try again or contact help for assistance.`,
              'Decline'
            );
        set_isBusy(false);
        set_showModalDeclinePrompt(false);
        set_isDeleted(isNotFoundStatusCodeOccurred);
        set_showModalIsDeleted(isNotFoundStatusCodeOccurred);
      });
  };

  /**
   *
   * @param {import('react').MouseEvent<HTMLElement> | KeyboardEvent} event
   * @returns
   */
  const expandCollapse = event => {
    event.defaultPrevented || event.type === 'click' ? set_isExpanded(!isExpanded) : void 0;
  };

  /**
   *
   * @param {{ reuseAction: 'stop'|'start' }} param0
   * @returns
   */
  const getResueActionLinkHandler = ({ reuseAction }) => {
    return event => {
      event.preventDefault();
      messages.clearByKeys('ReUse');
      set_showModalResueDiscard(reuseAction === 'stop');
      set_showModalReusePrompt(reuseAction === 'start');
    };
  };

  /**
   *
   * @param {Boolean} confirmed
   * @returns
   */
  const onModalResueNextChoiceResult = async confirmed => {
    if (!confirmed) {
      set_showModalReusePrompt(false);
      set_showModalResueDiscard(false);
      return;
    }

    const requestId = props.request?.id ?? null;
    const applicantName = _utils.getApplicantFullName();
    messages.clearByKeys('ReUse');

    set_isBusy(true);
    try {
      const response = await apiPUT(
        '/recommendation/rest/evaluationRequests/' + requestId + '/reuseForFutureEvaluations'
      );
      set_isBusy(false);
      set_showModalReusePrompt(false);
      set_showModalResueDiscard(false);
      messages.notifyGreen(
        response.body ? `Reuse is enabled for ${applicantName}.` : `Reuse is disabled for ${applicantName}.`,
        'ReUse'
      );
      props.onChanged?.();
    } catch (error) {
      messages.notifyRed(
        `The request to Re-Use this evaluation could not be processed at this time.
          Please try again or contact help for assistance.`,
        'ReUse'
      );
      set_isBusy(false);
      set_showModalReusePrompt(false);
      set_showModalResueDiscard(false);
      set_isDeleted(/404/.test(error.status));
      set_showModalIsDeleted(/404/.test(error.status));
    }
  };

  const onModalReopenRequestResult = async confirmed => {
    if (!confirmed) {
      set_showModalReopenPrompt(false);
      return;
    }

    set_isBusy(true);
    messages.clearByKeys('Reopen');
    const result = await _utils.actions.reopen();

    if (result.isReopened) {
      navigate(`/recommendationForm/${props.request.id}`);
    } else {
      messages.notifyRed(`Cannot edit this evaluation form.`, 'Reopen');
    }

    set_isBusy(false);
  };

  const downloadPDFClickHandler = event => {
    event ? event.preventDefault() : void 0;
    set_showModalPDF(true);
  };

  const startClickHandler = () => {
    openRequest();
  };

  const continueClickHandler = () => {
    openRequest();
  };

  const openRequest = () => {
    messages.clear();
    navigate(`/recommendationForm/${props.request.id}`);
  };

  const onModalResultOfPreviewPDF = () => {
    set_showModalPDF(false);
  };

  /**
   *
   * @param {import('../../../store/reducers/dashboardReducer').TEvaluationRequest} request
   */
  const renderReuseForButtons = request => {
    const { disableReuseEvaluation, isReusedForFutureEvaluation } = props;
    const evaluationName = RecommendationUtil.getEvaluationDisplayName(request).toSingleForm();
    const applicantName = RecommendationUtil.getApplicantFullName(request);
    const reuseEvaluationTooltip = `Reuse this type of ${applicantName} for all future ${request.casName} ${evaluationName} for ${applicantName} in this academic cycle.`;
    let result = null;
    if (RecommendationStatus.isReUseForFutureEvalsEnable(request)) {
      if (isReusedForFutureEvaluation) {
        result = (
          <Button
            primary
            link
            icon={<i className="fa fa-times"></i>}
            className={'lsn-reuse-stop-hyperlink'}
            onClick={getResueActionLinkHandler({ reuseAction: 'stop' })}
          >
            Stop Reusing
          </Button>
        );
      } else {
        result = (
          <Button
            primary
            link
            icon={<i className="fa fa-redo"></i>}
            className={'lsn-reuse-start-hyperlink'}
            onClick={getResueActionLinkHandler({ reuseAction: 'start' })}
            tooltip={reuseEvaluationTooltip}
            disabled={disableReuseEvaluation}
          >
            Reuse
          </Button>
        );
      }
    }
    return result;
  };

  /**
   *
   * @param {?string=} str
   * @param {?string[]=} words
   * @returns
   */
  const getHighlightedStr = (str, words) => {
    let htxt = str ?? '';
    for (const word of words ?? []) {
      htxt = htxt.replaceAll(new RegExp(word.replace(/<|>/g, ''), 'gi'), `<u class='found-word bg-yellow'>$&</u>`);
    }
    return htxt;
  };

  const render = () => {
    const { request, autoFiledRequests } = props;
    const applicantName = RecommendationUtil.getApplicantFullName(request);
    const evaluationName = RecommendationUtil.getEvaluationDisplayName(request).toLowerCase();
    const ariaLabelExpandText = [
      applicantName,
      startCase(camelCase(request.status)),
      `${request.evaluationType} - ${request.casName}`,
      'Expand button',
    ].join(' ');
    const ariaLabelCollapseText = [
      applicantName,
      startCase(camelCase(request.status)),
      `${request.evaluationType} - ${request.casName}`,
      'Collapse button',
    ].join(' ');

    return (
      <>
        <section
          className={[
            `panel lsn-recommendation-request-card lsn-request-card-${request.status}`,
            isDeleted ? 'lsn-request-card-deleted' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          tabIndex={0}
          id={`recommendation_${request.id}`}
          aria-label={[
            `Evaluation Request List Item for ${applicantName}.`,
            request.evaluationType + '.',
            `Status is ${request.status}.`,
            `Requested by ${moment(request.requestedByDate).format('MMM DD, YYYY')}.`,
            'List item is ' + isExpanded ? 'expanded' : 'collapsed',
          ].join(' ')}
          data-eval-status={isDeleted ? 'deleted' : request.status}
        >
          {isDeleted ? <div className="lsn-deleted-cover" title="Evaluation Request has been deleted" /> : void 0}
          <div className={'panel-header lsn-recommendation-header lsn-status-' + request.status}>
            {/* the toggle button */}
            <i
              role="button"
              tabIndex={0}
              onKeyDown={onKeyDownSpace(onKeyDownEnter(expandCollapse))}
              onClick={expandCollapse}
              aria-label={isExpanded ? ariaLabelExpandText : ariaLabelCollapseText}
              className={'fa fa-chevron ' + (isExpanded ? 'fa-chevron-down' : 'fa-chevron-right')}
            />
            <span
              className="panel-title lsn-recommendation-card-label"
              onKeyDown={onKeyDownSpace(onKeyDownEnter(expandCollapse))}
              onClick={expandCollapse}
              tabIndex={-1}
              aria-label={isExpanded ? ariaLabelExpandText : ariaLabelCollapseText}
            >
              <TextBody
                bold
                larger
                dangerouslySetInnerHTML={{
                  __html: getHighlightedStr(applicantName, props.highlightWords),
                }}
              ></TextBody>
              <TextBody dangerouslySetInnerHTML={{ __html: '&ndash;' }}></TextBody>
              <TextBody>
                {startCase(camelCase(request.status))} {request.accessedAsGuest ? ' as guest' : ''}
              </TextBody>

              {/* reuse tooltip */}
              {RecommendationStatus.isCompleted(request) && props.isReusedForFutureEvaluation ? (
                <Button
                  iconOnly
                  tooltip={`This type of ${applicantName} is being reused for all future ${request.casName} ${evaluationName} for ${applicantName} in this academic cycle.`}
                >
                  <i className="fas fa-share-all color-primary"></i>
                </Button>
              ) : (
                ''
              )}

              {/* eval type - cas name */}
              <TextBody
                dangerouslySetInnerHTML={{
                  __html: getHighlightedStr(`(${request.evaluationType} – ${request.casName})`, props.highlightWords),
                }}
              ></TextBody>
            </span>
            <div className="evaluation-controls text-right">
              {_utils.delegation.delegated && _utils.delegation.delegationDisabled ? (
                <div className="delegation-prohibited">
                  <i className="fa fa-exclamation-triangle"></i>
                  <div>
                    <strong>Delegation is not permitted for this CAS.</strong>
                    <span>You cannot complete or view completed evaluations.</span>
                  </div>
                </div>
              ) : _utils.getApplicantEmail()?.includes(auth.account?.email.emailAddress ?? '') ? (
                <div className="delegation-prohibited">
                  <i className="fa fa-exclamation-triangle"></i>
                  <div>
                    <span>You cannot complete an evaluation for yourself.</span>
                  </div>
                </div>
              ) : (
                [
                  renderReuseForButtons(request),
                  RecommendationStatus.isRequested(request) || RecommendationStatus.isAccepted(request) ? (
                    <Fragment>
                      <Button
                        danger
                        ghost
                        className={'lsn-decline-hyperlink'}
                        disabled={isDeleted}
                        onClick={() => {
                          messages.clearByKeys('Decline');
                          set_showModalDeclinePrompt(true);
                        }}
                      >
                        Decline
                      </Button>
                      {RecommendationStatus.isRequested(request) ? (
                        <Button primary onClick={startClickHandler} disabled={isDeleted}>
                          Start
                        </Button>
                      ) : (
                        <Button primary onClick={continueClickHandler} disabled={isDeleted}>
                          Continue
                        </Button>
                      )}
                    </Fragment>
                  ) : (
                    ''
                  ),
                  RecommendationStatus.isCompleted(request) && request.reopenable ? (
                    <Button
                      primary
                      link
                      onClick={() => set_showModalReopenPrompt(true)}
                      aria-label={`Edit Recommendation`}
                      tooltip="Edit Recommendation"
                      icon={<i className={'fa fa-pencil'} />}
                    >
                      Edit
                    </Button>
                  ) : (
                    ''
                  ),
                  RecommendationStatus.isCompleted(request) ? (
                    <Button
                      primary
                      link
                      onClick={downloadPDFClickHandler}
                      aria-label={`Download Recommendation as PDF`}
                      tooltip="Download Recommendation as PDF"
                      disabled={isDeleted}
                      icon={<i className={'fa fa-file-pdf'} />}
                    >
                      Download
                    </Button>
                  ) : (
                    ''
                  ),
                ]
              )}
            </div>
          </div>

          <div className={'lsn-panel-body ' + (isExpanded ? 'lsn-expand' : 'lsn-collapse')}>
            <div className="lsn-recommendation-list-items">
              <ul className="list">
                <li>
                  <TextBody bold>Applicant Email: </TextBody>
                  <a
                    tabIndex={0}
                    className="shrink-email"
                    href={'mailto:' + request.applicantEmail}
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedStr(request.applicantEmail, props.highlightWords),
                    }}
                  ></a>
                </li>
                <li>
                  <TextBody bold>Applying To: </TextBody>
                  <TextBody
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedStr(request.casName, props.highlightWords),
                    }}
                  ></TextBody>
                </li>
                {request.showOrgNameInLetters && request.organizationName && request.organizationLabel ? (
                  <li>
                    <TextBody
                      bold
                      dangerouslySetInnerHTML={{
                        __html: getHighlightedStr(request.organizationLabel + ': ', props.highlightWords),
                      }}
                      title={request.organizationLabel}
                    ></TextBody>
                    <TextBody
                      dangerouslySetInnerHTML={{
                        __html: getHighlightedStr(request.organizationName, props.highlightWords),
                      }}
                      title={request.organizationName}
                    ></TextBody>
                  </li>
                ) : null}
              </ul>
              <ul className="list">
                <li>
                  <TextBody bold>Type: </TextBody>
                  <TextBody
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedStr(request.evaluationType, props.highlightWords),
                    }}
                    title={request.evaluationType}
                  ></TextBody>
                </li>
                <li>
                  <TextBody bold>Access: </TextBody>
                  <TextBody
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedStr(
                        request.waiveRightOfAccess ? 'Waived' : 'Not Waived',
                        props.highlightWords
                      ),
                    }}
                    title={request.waiveRightOfAccess ? 'Waived' : 'Not Waived'}
                  ></TextBody>
                </li>
                {request.alternateId && request.alternateIdLabel ? (
                  <li>
                    <TextBody bold>{request.alternateIdLabel}: </TextBody>
                    <TextBody
                      dangerouslySetInnerHTML={{
                        __html: getHighlightedStr(request.alternateId, props.highlightWords),
                      }}
                      title={request.alternateId}
                    ></TextBody>
                  </li>
                ) : null}
              </ul>
              <ul className="list">
                <li>
                  <TextBody bold>Requested On: </TextBody>
                  <TextBody
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedStr(
                        moment(request.requestedOnDate).format('MMM DD, YYYY'),
                        props.highlightWords
                      ),
                    }}
                    title={moment(request.requestedOnDate).format('MMM DD, YYYY')}
                  ></TextBody>
                </li>
                <li>
                  <TextBody bold>Requested By: </TextBody>
                  <TextBody
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedStr(
                        moment(request.requestedByDate).format('MMM DD, YYYY'),
                        props.highlightWords
                      ),
                    }}
                    title={moment(request.requestedByDate).format('MMM DD, YYYY')}
                  ></TextBody>
                </li>
                {request.delegated ? (
                  <li>
                    <TextBody bold>Request Sent To: </TextBody>
                    <TextBody
                      dangerouslySetInnerHTML={{
                        __html: getHighlightedStr(request.evaluatorName, props.highlightWords),
                      }}
                      title={request.evaluatorName ?? ''}
                    ></TextBody>
                  </li>
                ) : null}
                {request.savedBy && request.savedBy !== request.evaluatorName ? (
                  <li>
                    <TextBody bold>{startCase(camelCase(request.status))} by: </TextBody>
                    <TextBody
                      dangerouslySetInnerHTML={{
                        __html: getHighlightedStr(request.savedBy, props.highlightWords),
                      }}
                      title={request.savedBy}
                    ></TextBody>
                  </li>
                ) : null}
              </ul>
            </div>
            <ProgramList programNames={request.programNames} highlightWords={props.highlightWords} />
            <AutoFilledRequests requests={autoFiledRequests} highlightWords={props.highlightWords} />
          </div>
        </section>

        <BaseModal
          closeIcon
          body={
            <TextBody>Evaluation Request has been deleted. Please refresh the page to see the updated list.</TextBody>
          }
          show={showModalIsDeleted}
          title={'Evaluation Request Deleted'}
          onClose={() => set_showModalIsDeleted(false)}
        />
        <ReuseRecommendationModal
          isBusy={isBusy}
          show={showModalReusePrompt}
          request={request}
          onClose={onModalResueNextChoiceResult}
        />
        <StopReuseRecommendationModal
          isBusy={isBusy}
          show={showModalResueDiscard}
          request={request}
          onClose={onModalResueNextChoiceResult}
        />
        <DeclineModal
          isBusy={isBusy}
          show={showModalDeclinePrompt}
          title={'Decline ' + request.applicantFirstName + "'s Request"}
          declineModalInfo={
            'You may leave an optional note to explain why you are declining ' +
            request.applicantFirstName +
            "'s request"
          }
          onClose={onModalDeclineRequestResult}
        />
        <PreviewModal
          show={showModalPDF}
          title={'Recommendation PDF'}
          subtitle={'Your PDF can be viewed below. You can save or print this PDF.'}
          evaluationId={request.id}
          buttonCancel={'Close'}
          onClose={onModalResultOfPreviewPDF}
        ></PreviewModal>

        <ReopenRecommendationModal
          show={showModalReopenPrompt}
          request={request}
          onClose={onModalReopenRequestResult}
          isBusy={isBusy}
        ></ReopenRecommendationModal>
      </>
    );
  };

  useEffect(() => {
    set_isExpanded(props.expandedState);
  }, [props.expandedState]);

  return render();
};

export default RequestCard;
